import type { LevelDef } from '../core/game';
import type { EnemyType } from '../core/types';
import type { WaveDef } from '../core/waves';
import { HUB_TOWER_HP } from './hubLevels';

/**
 * The Origin Event (spec: docs/superpowers/specs/2026-10-06-origin-event-design.md).
 *
 * It replaced the Capybara Event on The Hub's EVENTS tab: two portrait cards,
 * ABOVE THE CLOUDS (left — four levels since 2026-10-08) and THE DARK
 * SHADOWS (right — five). Each card opens its own level menu
 * (`OriginChapterScene`), whose red TEST button opens that chapter's arena.
 *
 * The levels live outside `LEVELS` and `HUB_LEVELS`: ids start at 201, so they
 * never move the campaign cursor (`hambi.unlocked`) or the Hub's
 * (`hambi.hub`). Their stars share `hambi.stars` (ids never collide), and a
 * level unlocks once the one before it in its chapter is won. They pay ONLY
 * the chapter's own currency — Dark Hambi Coins (`hambi.darkCoins`) for The
 * Dark Shadows, Light Hambi Coins (`hambi.lightCoins`) for Above the Clouds —
 * no coins, no rank EXP — on EVERY win.
 * Their best scores DO count toward the total score (ids 1–299).
 *
 * Every level runs the tower-HP system (`towerHp: 10`, as on The Moon) and
 * fields only the event's own enemies: the Ghost, The Dead and the Dark Ghost,
 * then (levels 3–5, spec 2026-10-07) the Dark Nacho, Jack'O Ghost, the Blue
 * Ghost and The Shadow Reaper.
 */

export type OriginChapterId = 'clouds' | 'shadows';

export interface OriginChapterDef {
  id: OriginChapterId;
  /** Shown under the card and as the menu's header. */
  name: string;
  /** Atlas key of the portrait cover art. */
  art: string;
  levels: LevelDef[];
}

/**
 * The Phantom Hambis lurking at the meadow's edges ("there are phantom hambis
 * lurking around the forest"): the tower-phantom-1 art, placed by GameScene's
 * `decor: 'phantoms'`. Every one is clear of the trail and the plots, pinned
 * by tests/unit/originEvent.test.ts.
 */
export const PHANTOM_DECOR: readonly { x: number; y: number }[] = [
  { x: 60, y: 110 },
  { x: 250, y: 650 },
  { x: 880, y: 70 },
  { x: 1010, y: 660 },
];

const SHADOWING_MEADOW: LevelDef = {
  id: 201,
  episode: 'I',
  name: 'The Shadowing Meadow',
  subtitle: 'The Dark Shadows',
  background: 'bg-shadowmeadow',
  decor: 'phantoms',
  // "Just like the Gloomy Forest": its route and plots, on the same clearing
  // (bg-shadowmeadow is drawn FROM bg-gloomyforest, layout kept).
  path: [
    { x: 0, y: 360 },
    { x: 180, y: 360 },
    { x: 180, y: 140 },
    { x: 420, y: 140 },
    { x: 420, y: 560 },
    { x: 660, y: 560 },
    { x: 660, y: 180 },
    { x: 900, y: 180 },
    { x: 900, y: 460 },
    { x: 1035, y: 460 },
  ],
  plots: [
    { id: 0, pos: { x: 80, y: 260 } },
    { id: 1, pos: { x: 80, y: 460 } },
    { id: 2, pos: { x: 300, y: 240 } },
    { id: 3, pos: { x: 320, y: 450 } },
    { id: 4, pos: { x: 420, y: 660 } },
    { id: 5, pos: { x: 540, y: 460 } },
    { id: 6, pos: { x: 540, y: 240 } },
    { id: 7, pos: { x: 540, y: 660 } },
    { id: 8, pos: { x: 780, y: 280 } },
    { id: 9, pos: { x: 780, y: 90 } },
    { id: 10, pos: { x: 760, y: 560 } },
    { id: 11, pos: { x: 1000, y: 280 } },
    { id: 12, pos: { x: 980, y: 100 } },
    { id: 13, pos: { x: 960, y: 570 } },
    { id: 14, pos: { x: 660, y: 660 } },
    { id: 15, pos: { x: 300, y: 350 } },
    { id: 16, pos: { x: 540, y: 350 } },
    { id: 17, pos: { x: 780, y: 460 } },
    { id: 18, pos: { x: 1000, y: 380 } },
    { id: 19, pos: { x: 540, y: 100 } },
  ],
  // The Moon's opening purse and lives — the brief gave none.
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — a few ghosts drift out of the dead trees.
    { groups: [{ enemyType: 'ghost', count: 3, interval: 3, startDelay: 0 }] },
    // 2 — more of them.
    { groups: [{ enemyType: 'ghost', count: 5, interval: 2.5, startDelay: 0 }] },
    // 3 — The Dead rise.
    {
      groups: [
        { enemyType: 'thedead', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'ghost', count: 2, interval: 3, startDelay: 3 },
      ],
    },
    // 4 — a ghost line.
    { groups: [{ enemyType: 'ghost', count: 6, interval: 2, startDelay: 0 }] },
    // 5 — The Dead with a ghost escort.
    {
      groups: [
        { enemyType: 'thedead', count: 3, interval: 3.5, startDelay: 0 },
        { enemyType: 'ghost', count: 3, interval: 2.5, startDelay: 4 },
      ],
    },
    // 6 — a haunting.
    { groups: [{ enemyType: 'ghost', count: 8, interval: 1.8, startDelay: 0 }] },
    // 7 — The Dead march.
    {
      groups: [
        { enemyType: 'thedead', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'ghost', count: 4, interval: 2, startDelay: 5 },
      ],
    },
    // 8 — ghosts first, The Dead behind them.
    {
      groups: [
        { enemyType: 'ghost', count: 6, interval: 1.6, startDelay: 0 },
        { enemyType: 'thedead', count: 3, interval: 3, startDelay: 6 },
      ],
    },
    // 9 — a wall of The Dead.
    { groups: [{ enemyType: 'thedead', count: 6, interval: 2.5, startDelay: 0 }] },
    // 10 — the meadow fills with ghosts.
    {
      groups: [
        { enemyType: 'ghost', count: 10, interval: 1.4, startDelay: 0 },
        { enemyType: 'thedead', count: 2, interval: 3, startDelay: 8 },
      ],
    },
    // 11 — both, overlapping.
    {
      groups: [
        { enemyType: 'thedead', count: 5, interval: 2.2, startDelay: 0 },
        { enemyType: 'ghost', count: 6, interval: 1.5, startDelay: 4 },
      ],
    },
    // 12 — a ghost rush.
    { groups: [{ enemyType: 'ghost', count: 12, interval: 1.2, startDelay: 0 }] },
    // 13 — The Dead in force.
    {
      groups: [
        { enemyType: 'thedead', count: 7, interval: 2, startDelay: 0 },
        { enemyType: 'ghost', count: 6, interval: 1.5, startDelay: 6 },
      ],
    },
    // 14 — everything, close together.
    {
      groups: [
        { enemyType: 'ghost', count: 10, interval: 1.2, startDelay: 0 },
        { enemyType: 'thedead', count: 6, interval: 2, startDelay: 5 },
      ],
    },
    // 15 — the last of the shadows.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 1.8, startDelay: 0 },
        { enemyType: 'ghost', count: 12, interval: 1, startDelay: 4 },
        { enemyType: 'thedead', count: 4, interval: 2, startDelay: 20 },
      ],
    },
  ],
};

const DARK_JUNGLE: LevelDef = {
  id: 202,
  episode: 'II',
  name: 'The Dark Jungle',
  subtitle: 'The Dark Shadows',
  background: 'bg-darkjungle',
  // The red trees crowd the four corners, so the route snakes through the
  // open middle band (y 180–540) and leaves mid-right.
  path: [
    { x: 0, y: 200 },
    { x: 260, y: 200 },
    { x: 260, y: 520 },
    { x: 520, y: 520 },
    { x: 520, y: 180 },
    { x: 780, y: 180 },
    { x: 780, y: 540 },
    { x: 960, y: 540 },
    { x: 960, y: 330 },
    { x: 1040, y: 330 },
  ],
  // Four plots in each corridor between the switchback legs, two by the exit.
  // Every plot is >= 50px off the trail and >= 120px from every other
  // (pinned by tests/unit/originEvent.test.ts).
  plots: [
    { id: 0, pos: { x: 130, y: 100 } },
    { id: 1, pos: { x: 130, y: 320 } },
    { id: 2, pos: { x: 130, y: 440 } },
    { id: 3, pos: { x: 130, y: 620 } },
    { id: 4, pos: { x: 390, y: 100 } },
    { id: 5, pos: { x: 390, y: 300 } },
    { id: 6, pos: { x: 390, y: 420 } },
    { id: 7, pos: { x: 390, y: 640 } },
    { id: 8, pos: { x: 650, y: 80 } },
    { id: 9, pos: { x: 650, y: 290 } },
    { id: 10, pos: { x: 650, y: 410 } },
    { id: 11, pos: { x: 650, y: 620 } },
    { id: 12, pos: { x: 870, y: 100 } },
    { id: 13, pos: { x: 870, y: 300 } },
    { id: 14, pos: { x: 870, y: 420 } },
    { id: 15, pos: { x: 870, y: 640 } },
    { id: 16, pos: { x: 1010, y: 200 } },
    { id: 17, pos: { x: 1010, y: 640 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — the jungle's ghosts.
    { groups: [{ enemyType: 'ghost', count: 5, interval: 2.5, startDelay: 0 }] },
    // 2 — The Dead among the red leaves.
    {
      groups: [
        { enemyType: 'thedead', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'ghost', count: 3, interval: 2.5, startDelay: 3 },
      ],
    },
    // 3 — the first Dark Ghost.
    {
      groups: [
        { enemyType: 'darkghost', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'ghost', count: 4, interval: 2, startDelay: 3 },
      ],
    },
    // 4 — a ghost line.
    { groups: [{ enemyType: 'ghost', count: 8, interval: 1.6, startDelay: 0 }] },
    // 5 — two Dark Ghosts and The Dead.
    {
      groups: [
        { enemyType: 'darkghost', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'thedead', count: 3, interval: 3, startDelay: 3 },
      ],
    },
    // 6 — The Dead march.
    {
      groups: [
        { enemyType: 'thedead', count: 5, interval: 2.5, startDelay: 0 },
        { enemyType: 'ghost', count: 4, interval: 2, startDelay: 5 },
      ],
    },
    // 7 — red and white ghosts together.
    {
      groups: [
        { enemyType: 'ghost', count: 6, interval: 1.6, startDelay: 0 },
        { enemyType: 'darkghost', count: 3, interval: 3, startDelay: 4 },
      ],
    },
    // 8 — a Dark Ghost wall.
    { groups: [{ enemyType: 'darkghost', count: 4, interval: 3, startDelay: 0 }] },
    // 9 — The Dead under ghost cover.
    {
      groups: [
        { enemyType: 'ghost', count: 8, interval: 1.4, startDelay: 0 },
        { enemyType: 'thedead', count: 5, interval: 2.2, startDelay: 4 },
      ],
    },
    // 10 — the jungle wakes.
    {
      groups: [
        { enemyType: 'darkghost', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'thedead', count: 4, interval: 2.5, startDelay: 5 },
        { enemyType: 'ghost', count: 6, interval: 1.5, startDelay: 10 },
      ],
    },
    // 11 — a ghost rush.
    { groups: [{ enemyType: 'ghost', count: 14, interval: 1.1, startDelay: 0 }] },
    // 12 — The Dead in force.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 2, startDelay: 0 },
        { enemyType: 'darkghost', count: 3, interval: 3, startDelay: 6 },
      ],
    },
    // 13 — Dark Ghosts, back to back.
    {
      groups: [
        { enemyType: 'darkghost', count: 6, interval: 2.2, startDelay: 0 },
        { enemyType: 'ghost', count: 6, interval: 1.4, startDelay: 5 },
      ],
    },
    // 14 — everything.
    {
      groups: [
        { enemyType: 'ghost', count: 10, interval: 1.2, startDelay: 0 },
        { enemyType: 'thedead', count: 6, interval: 2, startDelay: 4 },
        { enemyType: 'darkghost', count: 4, interval: 2.5, startDelay: 10 },
      ],
    },
    // 15 — the red tide.
    {
      groups: [
        { enemyType: 'darkghost', count: 8, interval: 1.8, startDelay: 0 },
        { enemyType: 'thedead', count: 6, interval: 2, startDelay: 6 },
      ],
    },
    // 16 — the heart of the dark.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 1.8, startDelay: 0 },
        { enemyType: 'ghost', count: 12, interval: 1, startDelay: 4 },
        { enemyType: 'darkghost', count: 8, interval: 1.6, startDelay: 12 },
      ],
    },
  ],
};

// ── The Dark Shadows, levels 3–5 (spec 2026-10-07) ──────────────────────────

/**
 * The ghosts haunting The Shadowing Temple ("lots of ghosts around the map"):
 * the event's own Ghost and Dark Ghost sprites, see-through and bobbing,
 * placed by GameScene's `decor: 'templeghosts'` on the walls, among the dark
 * trees and in the corners of the floor. Clear of the trail and the plots
 * (tests/unit/originEvent.test.ts).
 */
export const TEMPLE_GHOST_DECOR: readonly { x: number; y: number; dark: boolean }[] = [
  { x: 70, y: 140, dark: false },
  { x: 130, y: 330, dark: true },
  { x: 90, y: 560, dark: false },
  { x: 300, y: 70, dark: true },
  { x: 240, y: 640, dark: false },
  { x: 1010, y: 70, dark: false },
  { x: 1010, y: 520, dark: true },
  { x: 690, y: 700, dark: false },
];

/**
 * The red Phantom Hambis lurking in The Dark Forest (`decor-redphantom`,
 * placed by `decor: 'redphantoms'`), among the crystal trees at the edges.
 */
export const RED_PHANTOM_DECOR: readonly { x: number; y: number }[] = [
  { x: 40, y: 600 },
  { x: 240, y: 650 },
  { x: 640, y: 70 },
  { x: 1010, y: 400 },
  { x: 1010, y: 640 },
];

/**
 * The Quest for the Evil Orb's scenery (`decor: 'evilorb'`). In the middle of
 * the map, a small dead tree holds the orb in the fork of its branches — two
 * sprites, so the orb is never hidden by the tree and the win cutscene can
 * lift it out. The Shadow Reaper floats above it (his `anchorAt`), and the
 * haunts — red ghosts, ghosts and Phantom Hambis — stand round the edges.
 */
export const EVIL_ORB_DECOR = {
  tree: { x: 640, y: 400, size: 170 },
  orb: { x: 640, y: 352, size: 62 },
  haunts: [
    { x: 40, y: 80, kind: 'phantom' },
    { x: 40, y: 360, kind: 'darkghost' },
    { x: 190, y: 690, kind: 'ghost' },
    { x: 470, y: 690, kind: 'darkghost' },
    { x: 800, y: 690, kind: 'phantom' },
    { x: 1030, y: 330, kind: 'ghost' },
    { x: 1030, y: 700, kind: 'darkghost' },
    { x: 760, y: 70, kind: 'ghost' },
  ] as const satisfies readonly { x: number; y: number; kind: 'ghost' | 'darkghost' | 'phantom' }[],
};

const SHADOWING_TEMPLE: LevelDef = {
  id: 203,
  episode: 'III',
  name: 'The Shadowing Temple',
  subtitle: 'The Dark Shadows',
  background: 'bg-shadowtemple',
  decor: 'templeghosts',
  // "Melee towers get a 25% damage debuff from this level."
  meleeDamageMult: 0.75,
  notice: {
    art: 'enemy-darknacho',
    sender: 'The Shadowing Temple',
    text: 'The shadows weaken blades: melee towers deal 25% less damage in the temple.',
  },
  // "The track goes from the top of the screen to the bottom": in at the
  // temple's door (top centre), switchbacks down the courtyard, out the bottom.
  path: [
    { x: 640, y: 0 },
    { x: 640, y: 120 },
    { x: 330, y: 120 },
    { x: 330, y: 320 },
    { x: 880, y: 320 },
    { x: 880, y: 520 },
    { x: 520, y: 520 },
    { x: 520, y: 720 },
  ],
  plots: [
    { id: 0, pos: { x: 200, y: 80 } },
    { id: 1, pos: { x: 790, y: 90 } },
    { id: 2, pos: { x: 230, y: 210 } },
    { id: 3, pos: { x: 440, y: 220 } },
    { id: 4, pos: { x: 600, y: 220 } },
    { id: 5, pos: { x: 760, y: 220 } },
    { id: 6, pos: { x: 940, y: 230 } },
    { id: 7, pos: { x: 240, y: 400 } },
    { id: 8, pos: { x: 420, y: 420 } },
    { id: 9, pos: { x: 600, y: 420 } },
    { id: 10, pos: { x: 760, y: 420 } },
    { id: 11, pos: { x: 980, y: 420 } },
    { id: 12, pos: { x: 400, y: 600 } },
    { id: 13, pos: { x: 640, y: 620 } },
    { id: 14, pos: { x: 800, y: 620 } },
    { id: 15, pos: { x: 960, y: 620 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — ghosts drift in through the temple door.
    { groups: [{ enemyType: 'ghost', count: 5, interval: 2.5, startDelay: 0 }] },
    // 2 — The Dead and their ghosts.
    {
      groups: [
        { enemyType: 'thedead', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'ghost', count: 3, interval: 2.5, startDelay: 3 },
      ],
    },
    // 3 — the first Dark Nacho.
    {
      groups: [
        { enemyType: 'darknacho', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'ghost', count: 4, interval: 2, startDelay: 3 },
      ],
    },
    // 4 — red ghosts.
    {
      groups: [
        { enemyType: 'darkghost', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'thedead', count: 2, interval: 3, startDelay: 4 },
      ],
    },
    // 5 — two Dark Nachos.
    {
      groups: [
        { enemyType: 'darknacho', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'ghost', count: 5, interval: 1.8, startDelay: 2 },
      ],
    },
    // 6 — The Dead march down the courtyard.
    { groups: [{ enemyType: 'thedead', count: 6, interval: 2.2, startDelay: 0 }] },
    // 7 — nachos and red ghosts.
    {
      groups: [
        { enemyType: 'darkghost', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'darknacho', count: 2, interval: 3.5, startDelay: 5 },
      ],
    },
    // 8 — a ghost rush.
    { groups: [{ enemyType: 'ghost', count: 12, interval: 1.2, startDelay: 0 }] },
    // 9 — everything so far.
    {
      groups: [
        { enemyType: 'thedead', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'darkghost', count: 3, interval: 2.5, startDelay: 4 },
        { enemyType: 'darknacho', count: 2, interval: 3, startDelay: 9 },
      ],
    },
    // 10 — a Dark Nacho column.
    {
      groups: [
        { enemyType: 'darknacho', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'ghost', count: 6, interval: 1.5, startDelay: 3 },
      ],
    },
    // 11 — The Dead in force.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 1.8, startDelay: 0 },
        { enemyType: 'darkghost', count: 4, interval: 2.2, startDelay: 6 },
      ],
    },
    // 12 — a red tide.
    {
      groups: [
        { enemyType: 'darkghost', count: 8, interval: 1.6, startDelay: 0 },
        { enemyType: 'darknacho', count: 2, interval: 3, startDelay: 6 },
      ],
    },
    // 13 — nachos behind a ghost screen.
    {
      groups: [
        { enemyType: 'ghost', count: 10, interval: 1.1, startDelay: 0 },
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 5 },
      ],
    },
    // 14 — the temple wakes.
    {
      groups: [
        { enemyType: 'thedead', count: 6, interval: 2, startDelay: 0 },
        { enemyType: 'darkghost', count: 6, interval: 1.8, startDelay: 4 },
        { enemyType: 'darknacho', count: 3, interval: 3, startDelay: 10 },
      ],
    },
    // 15 — a wall of Dark Nachos.
    {
      groups: [
        { enemyType: 'darknacho', count: 6, interval: 2.4, startDelay: 0 },
        { enemyType: 'ghost', count: 8, interval: 1.2, startDelay: 4 },
      ],
    },
    // 16 — everything, close together.
    {
      groups: [
        { enemyType: 'darkghost', count: 8, interval: 1.4, startDelay: 0 },
        { enemyType: 'thedead', count: 6, interval: 1.8, startDelay: 4 },
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 9 },
      ],
    },
    // 17 — the last of the temple's shadows.
    {
      groups: [
        { enemyType: 'ghost', count: 12, interval: 1, startDelay: 0 },
        { enemyType: 'darknacho', count: 6, interval: 2.2, startDelay: 4 },
        { enemyType: 'darkghost', count: 8, interval: 1.5, startDelay: 10 },
        { enemyType: 'thedead', count: 6, interval: 1.8, startDelay: 18 },
      ],
    },
  ],
};

const DARK_FOREST: LevelDef = {
  id: 204,
  episode: 'IV',
  name: 'The Dark Forest',
  subtitle: 'The Dark Shadows',
  background: 'bg-darkforest',
  decor: 'redphantoms',
  // The crystal trees crowd the corners; the route snakes the open middle.
  path: [
    { x: 0, y: 360 },
    { x: 200, y: 360 },
    { x: 200, y: 140 },
    { x: 500, y: 140 },
    { x: 500, y: 580 },
    { x: 800, y: 580 },
    { x: 800, y: 200 },
    { x: 1040, y: 200 },
  ],
  plots: [
    { id: 0, pos: { x: 100, y: 80 } },
    { id: 1, pos: { x: 330, y: 80 } },
    { id: 2, pos: { x: 700, y: 130 } },
    { id: 3, pos: { x: 920, y: 90 } },
    { id: 4, pos: { x: 100, y: 260 } },
    { id: 5, pos: { x: 320, y: 250 } },
    { id: 6, pos: { x: 600, y: 250 } },
    { id: 7, pos: { x: 900, y: 300 } },
    { id: 8, pos: { x: 700, y: 330 } },
    { id: 9, pos: { x: 390, y: 400 } },
    { id: 10, pos: { x: 600, y: 420 } },
    { id: 11, pos: { x: 100, y: 460 } },
    { id: 12, pos: { x: 900, y: 480 } },
    { id: 13, pos: { x: 380, y: 560 } },
    { id: 14, pos: { x: 420, y: 680 } },
    { id: 15, pos: { x: 650, y: 680 } },
    { id: 16, pos: { x: 900, y: 680 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — ghosts among the roots.
    { groups: [{ enemyType: 'ghost', count: 6, interval: 2.2, startDelay: 0 }] },
    // 2 — The Dead.
    {
      groups: [
        { enemyType: 'thedead', count: 4, interval: 2.8, startDelay: 0 },
        { enemyType: 'ghost', count: 3, interval: 2.2, startDelay: 4 },
      ],
    },
    // 3 — red ghosts.
    { groups: [{ enemyType: 'darkghost', count: 4, interval: 2.6, startDelay: 0 }] },
    // 4 — a Dark Nacho.
    {
      groups: [
        { enemyType: 'darknacho', count: 2, interval: 4, startDelay: 0 },
        { enemyType: 'ghost', count: 5, interval: 1.8, startDelay: 2 },
      ],
    },
    // 5 — The Dead with red ghosts.
    {
      groups: [
        { enemyType: 'thedead', count: 5, interval: 2.2, startDelay: 0 },
        { enemyType: 'darkghost', count: 3, interval: 2.5, startDelay: 5 },
      ],
    },
    // 6 — a ghost line.
    { groups: [{ enemyType: 'ghost', count: 12, interval: 1.2, startDelay: 0 }] },
    // 7 — nachos.
    {
      groups: [
        { enemyType: 'darknacho', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'thedead', count: 3, interval: 2.5, startDelay: 4 },
      ],
    },
    // 8 — the red tide.
    {
      groups: [
        { enemyType: 'darkghost', count: 7, interval: 1.8, startDelay: 0 },
        { enemyType: 'ghost', count: 5, interval: 1.4, startDelay: 5 },
      ],
    },
    // 9 — everything so far.
    {
      groups: [
        { enemyType: 'thedead', count: 5, interval: 2, startDelay: 0 },
        { enemyType: 'darknacho', count: 3, interval: 3, startDelay: 4 },
        { enemyType: 'darkghost', count: 4, interval: 2, startDelay: 10 },
      ],
    },
    // 10 — a nacho column.
    {
      groups: [
        { enemyType: 'darknacho', count: 5, interval: 2.6, startDelay: 0 },
        { enemyType: 'ghost', count: 8, interval: 1.2, startDelay: 3 },
      ],
    },
    // 11 — The Dead in force.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 1.8, startDelay: 0 },
        { enemyType: 'darkghost', count: 5, interval: 2, startDelay: 6 },
      ],
    },
    // 12 — the forest closes in.
    {
      groups: [
        { enemyType: 'darkghost', count: 8, interval: 1.5, startDelay: 0 },
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 13 — "only after wave 12": the first Jack'O Ghost.
    {
      groups: [
        { enemyType: 'jackoghost', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'ghost', count: 8, interval: 1.3, startDelay: 3 },
      ],
    },
    // 14 — two Jack'Os and The Dead.
    {
      groups: [
        { enemyType: 'jackoghost', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'thedead', count: 6, interval: 2, startDelay: 3 },
      ],
    },
    // 15 — pumpkins and nachos.
    {
      groups: [
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 0 },
        { enemyType: 'jackoghost', count: 2, interval: 4, startDelay: 5 },
        { enemyType: 'darkghost', count: 6, interval: 1.6, startDelay: 8 },
      ],
    },
    // 16 — "two Blue Ghosts every wave after 15".
    {
      groups: [
        { enemyType: 'blueghost', count: 2, interval: 6, startDelay: 0 },
        { enemyType: 'jackoghost', count: 2, interval: 4, startDelay: 4 },
        { enemyType: 'ghost', count: 10, interval: 1.1, startDelay: 6 },
      ],
    },
    // 17 — the crystal trees' guard.
    {
      groups: [
        { enemyType: 'thedead', count: 6, interval: 1.8, startDelay: 0 },
        { enemyType: 'blueghost', count: 2, interval: 6, startDelay: 4 },
        { enemyType: 'jackoghost', count: 3, interval: 3.5, startDelay: 8 },
        { enemyType: 'darknacho', count: 3, interval: 3, startDelay: 12 },
      ],
    },
    // 18 — the heart of the dark forest.
    {
      groups: [
        { enemyType: 'darkghost', count: 10, interval: 1.3, startDelay: 0 },
        { enemyType: 'blueghost', count: 2, interval: 6, startDelay: 5 },
        { enemyType: 'jackoghost', count: 3, interval: 3.5, startDelay: 8 },
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 14 },
      ],
    },
  ],
};

const EVIL_ORB_QUEST: LevelDef = {
  id: 205,
  episode: 'V',
  name: 'The Quest for the Evil Orb',
  subtitle: 'The Dark Shadows',
  background: 'bg-evilorbforest',
  decor: 'evilorb',
  // Two tall loops round the middle, leaving the dead tree with the orb (centre)
  // and The Shadow Reaper's spot above it (top centre) untouched.
  path: [
    { x: 0, y: 560 },
    { x: 180, y: 560 },
    { x: 180, y: 140 },
    { x: 420, y: 140 },
    { x: 420, y: 600 },
    { x: 860, y: 600 },
    { x: 860, y: 140 },
    { x: 1040, y: 140 },
  ],
  plots: [
    { id: 0, pos: { x: 300, y: 80 } },
    { id: 1, pos: { x: 980, y: 80 } },
    { id: 2, pos: { x: 80, y: 250 } },
    { id: 3, pos: { x: 300, y: 260 } },
    { id: 4, pos: { x: 520, y: 250 } },
    { id: 5, pos: { x: 760, y: 250 } },
    { id: 6, pos: { x: 960, y: 260 } },
    { id: 7, pos: { x: 300, y: 420 } },
    { id: 8, pos: { x: 960, y: 420 } },
    { id: 9, pos: { x: 80, y: 450 } },
    { id: 10, pos: { x: 520, y: 500 } },
    { id: 11, pos: { x: 760, y: 500 } },
    { id: 12, pos: { x: 960, y: 600 } },
    { id: 13, pos: { x: 80, y: 660 } },
    { id: 14, pos: { x: 300, y: 660 } },
    { id: 15, pos: { x: 640, y: 680 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — the orb calls the ghosts.
    { groups: [{ enemyType: 'ghost', count: 6, interval: 2, startDelay: 0 }] },
    // 2 — The Dead.
    {
      groups: [
        { enemyType: 'thedead', count: 4, interval: 2.6, startDelay: 0 },
        { enemyType: 'ghost', count: 4, interval: 2, startDelay: 4 },
      ],
    },
    // 3 — red ghosts.
    { groups: [{ enemyType: 'darkghost', count: 5, interval: 2.4, startDelay: 0 }] },
    // 4 — Dark Nachos.
    {
      groups: [
        { enemyType: 'darknacho', count: 2, interval: 3.5, startDelay: 0 },
        { enemyType: 'thedead', count: 3, interval: 2.5, startDelay: 3 },
      ],
    },
    // 5 — a Jack'O Ghost.
    {
      groups: [
        { enemyType: 'jackoghost', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'ghost', count: 8, interval: 1.4, startDelay: 2 },
      ],
    },
    // 6 — The Dead march.
    {
      groups: [
        { enemyType: 'thedead', count: 7, interval: 2, startDelay: 0 },
        { enemyType: 'darkghost', count: 3, interval: 2.5, startDelay: 5 },
      ],
    },
    // 7 — nachos and pumpkins.
    {
      groups: [
        { enemyType: 'darknacho', count: 3, interval: 3, startDelay: 0 },
        { enemyType: 'jackoghost', count: 2, interval: 4, startDelay: 4 },
      ],
    },
    // 8 — a Blue Ghost.
    {
      groups: [
        { enemyType: 'blueghost', count: 1, interval: 1, startDelay: 0 },
        { enemyType: 'darkghost', count: 6, interval: 1.6, startDelay: 3 },
      ],
    },
    // 9 — a ghost rush.
    { groups: [{ enemyType: 'ghost', count: 16, interval: 1, startDelay: 0 }] },
    // 10 — everything so far.
    {
      groups: [
        { enemyType: 'thedead', count: 5, interval: 2, startDelay: 0 },
        { enemyType: 'darknacho', count: 3, interval: 2.8, startDelay: 4 },
        { enemyType: 'jackoghost', count: 2, interval: 4, startDelay: 8 },
        { enemyType: 'darkghost', count: 4, interval: 2, startDelay: 12 },
      ],
    },
    // 11 — a nacho column.
    {
      groups: [
        { enemyType: 'darknacho', count: 5, interval: 2.5, startDelay: 0 },
        { enemyType: 'ghost', count: 8, interval: 1.2, startDelay: 3 },
      ],
    },
    // 12 — blue and orange.
    {
      groups: [
        { enemyType: 'blueghost', count: 2, interval: 5, startDelay: 0 },
        { enemyType: 'jackoghost', count: 3, interval: 3.5, startDelay: 3 },
      ],
    },
    // 13 — The Dead in force.
    {
      groups: [
        { enemyType: 'thedead', count: 10, interval: 1.6, startDelay: 0 },
        { enemyType: 'darkghost', count: 6, interval: 1.8, startDelay: 6 },
      ],
    },
    // 14 — the red tide.
    {
      groups: [
        { enemyType: 'darkghost', count: 10, interval: 1.3, startDelay: 0 },
        { enemyType: 'darknacho', count: 4, interval: 2.5, startDelay: 6 },
      ],
    },
    // 15 — every ghost there is.
    {
      groups: [
        { enemyType: 'ghost', count: 10, interval: 1, startDelay: 0 },
        { enemyType: 'jackoghost', count: 3, interval: 3, startDelay: 4 },
        { enemyType: 'blueghost', count: 2, interval: 5, startDelay: 8 },
        { enemyType: 'darkghost', count: 6, interval: 1.5, startDelay: 12 },
      ],
    },
    // 16 — nachos behind The Dead.
    {
      groups: [
        { enemyType: 'thedead', count: 8, interval: 1.6, startDelay: 0 },
        { enemyType: 'darknacho', count: 5, interval: 2.4, startDelay: 5 },
      ],
    },
    // 17 — the forest stirs.
    {
      groups: [
        { enemyType: 'jackoghost', count: 4, interval: 3, startDelay: 0 },
        { enemyType: 'blueghost', count: 2, interval: 5, startDelay: 5 },
        { enemyType: 'ghost', count: 12, interval: 1, startDelay: 8 },
      ],
    },
    // 18 — a wall of red.
    {
      groups: [
        { enemyType: 'darkghost', count: 12, interval: 1.2, startDelay: 0 },
        { enemyType: 'darknacho', count: 5, interval: 2.2, startDelay: 6 },
        { enemyType: 'thedead', count: 6, interval: 1.6, startDelay: 12 },
      ],
    },
    // 19 — the last guard before the orb.
    {
      groups: [
        { enemyType: 'darknacho', count: 6, interval: 2.2, startDelay: 0 },
        { enemyType: 'blueghost', count: 3, interval: 4, startDelay: 4 },
        { enemyType: 'jackoghost', count: 4, interval: 3, startDelay: 8 },
        { enemyType: 'darkghost', count: 8, interval: 1.4, startDelay: 14 },
      ],
    },
    // 20 — THE SHADOW REAPER comes down from the top of the screen, ALONE.
    // Every tower turns on him the moment he lands (anchored, `ignoreRange`,
    // `remaining` 0 — Prince Nacho's second form's rules), so anything sent
    // with him would walk past an army looking the other way.
    { groups: [{ enemyType: 'reaper', count: 1, interval: 1, startDelay: 0 }] },
  ],
};

// ── Above the Clouds (spec 2026-10-08-above-the-clouds-design.md) ─────────
// Four levels, paid in LIGHT Hambi Coins. Every route and plot is laid on the
// art: on the cloud floor below the sky band (the Sky Up High, Sunset
// Horizon), inside the pitch (Rainbow Arena), on the islands and the clouds
// over them (The Sky Islands). Pinned by tests/unit/originEvent.test.ts.

/** `{ enemyType, count, interval, startDelay }` without the noise. */
const g = (
  enemyType: EnemyType,
  count: number,
  interval: number,
  startDelay = 0,
): WaveDef['groups'][number] => ({ enemyType, count, interval, startDelay });

const SKY_UP_HIGH: LevelDef = {
  id: 211,
  episode: 'I',
  name: 'The Sky Up High',
  subtitle: 'Above the Clouds',
  background: 'bg-skyhigh',
  // Night sky across the top fifth; everything below is unbroken cloud, so
  // the whole route stays below y 200 and every plot below y 175.
  path: [
    { x: 0, y: 250 },
    { x: 880, y: 250 },
    { x: 880, y: 420 },
    { x: 200, y: 420 },
    { x: 200, y: 590 },
    { x: 760, y: 590 },
    { x: 760, y: 720 },
  ],
  plots: [
    { id: 0, pos: { x: 360, y: 180 } },
    { id: 1, pos: { x: 700, y: 180 } },
    { id: 2, pos: { x: 120, y: 335 } },
    { id: 3, pos: { x: 300, y: 335 } },
    { id: 4, pos: { x: 440, y: 335 } },
    { id: 5, pos: { x: 580, y: 335 } },
    { id: 6, pos: { x: 720, y: 335 } },
    { id: 7, pos: { x: 980, y: 335 } },
    { id: 8, pos: { x: 320, y: 505 } },
    { id: 9, pos: { x: 460, y: 505 } },
    { id: 10, pos: { x: 600, y: 505 } },
    { id: 11, pos: { x: 740, y: 505 } },
    { id: 12, pos: { x: 960, y: 505 } },
    { id: 13, pos: { x: 400, y: 665 } },
    { id: 14, pos: { x: 560, y: 665 } },
    { id: 15, pos: { x: 900, y: 660 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    // 1 — Wingers over the cloud tops.
    { groups: [g('winger', 5, 2.5)] },
    // 2 — the first Twilights.
    { groups: [g('twilight', 3, 3), g('winger', 3, 2.5, 3)] },
    { groups: [g('winger', 8, 1.8)] },
    { groups: [g('twilight', 5, 2.5)] },
    { groups: [g('winger', 6, 2), g('twilight', 3, 3, 4)] },
    { groups: [g('twilight', 6, 2)] },
    { groups: [g('winger', 12, 1.3)] },
    { groups: [g('winger', 6, 1.8), g('twilight', 5, 2.2, 3)] },
    { groups: [g('twilight', 8, 1.8)] },
    { groups: [g('winger', 10, 1.4), g('twilight', 6, 2, 5)] },
    { groups: [g('twilight', 10, 1.6)] },
    { groups: [g('winger', 16, 1.1)] },
    { groups: [g('twilight', 8, 1.6), g('winger', 10, 1.2, 4)] },
    { groups: [g('twilight', 12, 1.4), g('winger', 8, 1.2, 6)] },
    // 15 — the night's whole flock.
    { groups: [g('winger', 14, 1), g('twilight', 14, 1.3, 4)] },
  ],
};

const RAINBOW_ARENA: LevelDef = {
  id: 212,
  episode: 'II',
  name: 'Rainbow Arena',
  subtitle: 'Above the Clouds',
  background: 'bg-rainbowarena',
  // Inside the round pitch: in from its left side, four long columns up and
  // down the middle, out under the tower panel on the right. Twilights watch
  // from the stands.
  path: [
    { x: 120, y: 330 },
    { x: 250, y: 330 },
    { x: 250, y: 140 },
    { x: 470, y: 140 },
    { x: 470, y: 590 },
    { x: 690, y: 590 },
    { x: 690, y: 140 },
    { x: 900, y: 140 },
    { x: 900, y: 560 },
    { x: 1080, y: 560 },
  ],
  plots: [
    { id: 0, pos: { x: 150, y: 460 } },
    { id: 1, pos: { x: 360, y: 260 } },
    { id: 2, pos: { x: 360, y: 400 } },
    { id: 3, pos: { x: 360, y: 540 } },
    { id: 4, pos: { x: 580, y: 220 } },
    { id: 5, pos: { x: 580, y: 360 } },
    { id: 6, pos: { x: 580, y: 500 } },
    { id: 7, pos: { x: 580, y: 660 } },
    { id: 8, pos: { x: 795, y: 260 } },
    { id: 9, pos: { x: 795, y: 400 } },
    { id: 10, pos: { x: 795, y: 540 } },
    { id: 11, pos: { x: 1000, y: 280 } },
    { id: 12, pos: { x: 1000, y: 420 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    { groups: [g('winger', 5, 2.5)] },
    { groups: [g('twilight', 3, 3), g('winger', 3, 2.5, 3)] },
    // 3 — the first Nightflies.
    { groups: [g('nightfly', 2, 4), g('winger', 3, 2.5, 3)] },
    { groups: [g('twilight', 5, 2.2)] },
    { groups: [g('nightfly', 3, 3), g('winger', 5, 2, 2)] },
    { groups: [g('winger', 10, 1.5)] },
    { groups: [g('nightfly', 4, 2.5), g('twilight', 4, 2, 4)] },
    { groups: [g('twilight', 7, 1.8), g('winger', 5, 1.6, 4)] },
    { groups: [g('nightfly', 6, 2.2)] },
    { groups: [g('winger', 10, 1.3), g('nightfly', 4, 2.5, 5)] },
    { groups: [g('twilight', 8, 1.6), g('nightfly', 4, 2.4, 6)] },
    { groups: [g('nightfly', 8, 1.8)] },
    { groups: [g('winger', 12, 1.1), g('twilight', 6, 1.8, 5)] },
    { groups: [g('nightfly', 8, 1.8), g('twilight', 6, 1.6, 4)] },
    { groups: [g('winger', 10, 1), g('nightfly', 8, 1.7, 4)] },
    // 16 — a full house.
    { groups: [g('nightfly', 10, 1.5), g('twilight', 8, 1.4, 5), g('winger', 8, 1, 12)] },
  ],
};

const SUNSET_HORIZON: LevelDef = {
  id: 213,
  episode: 'III',
  name: 'Sunset Horizon',
  subtitle: 'Above the Clouds',
  background: 'bg-sunsethorizon',
  // The Sky Up High's floor at sunset: three columns up and down the yellow
  // clouds, below the sun's band, out at the bottom.
  path: [
    { x: 0, y: 250 },
    { x: 260, y: 250 },
    { x: 260, y: 600 },
    { x: 520, y: 600 },
    { x: 520, y: 260 },
    { x: 780, y: 260 },
    { x: 780, y: 600 },
    { x: 960, y: 600 },
    { x: 960, y: 720 },
  ],
  plots: [
    { id: 0, pos: { x: 130, y: 380 } },
    { id: 1, pos: { x: 130, y: 520 } },
    { id: 2, pos: { x: 130, y: 660 } },
    { id: 3, pos: { x: 390, y: 330 } },
    { id: 4, pos: { x: 390, y: 470 } },
    { id: 5, pos: { x: 390, y: 680 } },
    { id: 6, pos: { x: 650, y: 380 } },
    { id: 7, pos: { x: 650, y: 520 } },
    { id: 8, pos: { x: 650, y: 680 } },
    { id: 9, pos: { x: 870, y: 330 } },
    { id: 10, pos: { x: 870, y: 470 } },
    { id: 11, pos: { x: 1010, y: 400 } },
    { id: 12, pos: { x: 1010, y: 520 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    { groups: [g('winger', 6, 2.2)] },
    { groups: [g('twilight', 4, 2.5)] },
    // 3 — the first Halolight, and what he breaks into.
    { groups: [g('halolight', 1, 1), g('winger', 3, 2, 3)] },
    { groups: [g('nightfly', 3, 3)] },
    { groups: [g('halolight', 1, 1), g('twilight', 4, 2.2, 3)] },
    { groups: [g('winger', 10, 1.4)] },
    { groups: [g('twilight', 5, 2), g('nightfly', 3, 2.5, 4)] },
    { groups: [g('halolight', 2, 5), g('winger', 5, 1.6, 4)] },
    { groups: [g('nightfly', 7, 2)] },
    { groups: [g('halolight', 2, 4), g('twilight', 5, 1.8, 5)] },
    { groups: [g('winger', 12, 1.1), g('nightfly', 4, 2.4, 6)] },
    { groups: [g('halolight', 3, 4), g('nightfly', 4, 2.2, 6)] },
    { groups: [g('twilight', 8, 1.5), g('winger', 8, 1.1, 4)] },
    { groups: [g('halolight', 3, 3.5), g('winger', 6, 1.2, 5)] },
    { groups: [g('nightfly', 8, 1.6), g('twilight', 6, 1.5, 5)] },
    { groups: [g('halolight', 3, 3.5), g('nightfly', 5, 1.8, 6)] },
    // 17 — the sun goes down on everything.
    {
      groups: [
        g('halolight', 3, 3.5),
        g('nightfly', 8, 1.6, 6),
        g('twilight', 8, 1.3, 12),
        g('winger', 10, 1, 18),
      ],
    },
  ],
};

const SKY_ISLANDS: LevelDef = {
  id: 214,
  episode: 'IV',
  name: 'The Sky Islands',
  subtitle: 'Above the Clouds',
  background: 'bg-skyislands',
  // In across the left island, a loop over the big island's open middle (clear
  // of its trees and both waterfalls), across the right island, then down off
  // its edge and back the whole width of the board low along the cliffs —
  // every enemy here has wings — out at the left. Plots on the islands
  // between the two lanes, and on the water under the return lane.
  path: [
    { x: 0, y: 420 },
    { x: 330, y: 420 },
    { x: 330, y: 260 },
    { x: 780, y: 260 },
    { x: 780, y: 420 },
    { x: 1000, y: 420 },
    { x: 1000, y: 560 },
    { x: 0, y: 560 },
  ],
  plots: [
    // The big island: inside the loop, and on its front lip.
    { id: 0, pos: { x: 450, y: 325 } },
    { id: 1, pos: { x: 570, y: 325 } },
    { id: 2, pos: { x: 690, y: 325 } },
    { id: 3, pos: { x: 630, y: 490 } },
    // The left island, in front of its three trees.
    { id: 4, pos: { x: 110, y: 490 } },
    { id: 5, pos: { x: 230, y: 490 } },
    // The right island, in front of its trees.
    { id: 6, pos: { x: 870, y: 490 } },
    // The clouds over the right of the board.
    { id: 7, pos: { x: 900, y: 200 } },
    // The sea, under the return lane.
    { id: 8, pos: { x: 120, y: 640 } },
    { id: 9, pos: { x: 240, y: 640 } },
    { id: 10, pos: { x: 560, y: 640 } },
    { id: 11, pos: { x: 700, y: 640 } },
    { id: 12, pos: { x: 860, y: 640 } },
    { id: 13, pos: { x: 980, y: 640 } },
  ],
  startCash: 1500,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    { groups: [g('winger', 5, 2.5)] },
    { groups: [g('twilight', 3, 2.5), g('winger', 3, 2, 4)] },
    { groups: [g('nightfly', 3, 3)] },
    { groups: [g('halolight', 1, 1), g('winger', 4, 1.8, 3)] },
    { groups: [g('twilight', 6, 1.8)] },
    { groups: [g('nightfly', 5, 2.2), g('winger', 5, 1.6, 4)] },
    { groups: [g('halolight', 2, 5), g('twilight', 3, 2, 5)] },
    { groups: [g('winger', 12, 1.2)] },
    { groups: [g('nightfly', 6, 1.9), g('twilight', 4, 1.8, 6)] },
    { groups: [g('halolight', 3, 4), g('nightfly', 3, 2.4, 6)] },
    { groups: [g('twilight', 10, 1.4)] },
    { groups: [g('winger', 10, 1.1), g('halolight', 2, 4, 5)] },
    { groups: [g('nightfly', 8, 1.7), g('twilight', 5, 1.6, 5)] },
    { groups: [g('halolight', 2, 4), g('winger', 6, 1.2, 5)] },
    { groups: [g('twilight', 7, 1.5), g('nightfly', 5, 1.8, 5)] },
    { groups: [g('halolight', 2, 4), g('twilight', 5, 1.6, 6)] },
    { groups: [g('nightfly', 8, 1.6), g('winger', 10, 1.1, 5)] },
    // 18 — the whole sky at once.
    {
      groups: [
        g('halolight', 2, 4),
        g('nightfly', 6, 1.6, 8),
        g('twilight', 8, 1.3, 14),
        g('winger', 10, 1, 20),
      ],
    },
  ],
};

/**
 * Heaven's Peak's centrepiece (spec 2026-10-12): the light blue-white orb and
 * the ring of sakura trees around it, placed by the game over bg-heavenspeak —
 * the generator cannot see the track, and the track has to circle them.
 * Pinned against the route by tests/unit/originEvent.test.ts.
 */
export const HEAVENS_PEAK_RING = {
  centre: { x: 640, y: 320 },
  /** The trail's radius around the centre. */
  trackRadius: 190,
  /** The trees' radius — clear inside the trail. */
  treeRadius: 100,
  trees: 8,
} as const;

const HEAVENS_PEAK: LevelDef = {
  id: 215,
  episode: 'V',
  name: "Heaven's Peak",
  subtitle: 'Above the Clouds',
  background: 'bg-heavenspeak',
  decor: 'heavenspeak',
  // Two bases: one at the left edge, one on the clouds at the right (just
  // clear of the tower panel, which would hide a base at the right edge).
  // Each lane climbs and runs along the top to meet the other over the
  // sakura ring (both 740 px, pinned). The single trunk then circles the ring clockwise from the top —
  // right, down past the sea, round the bottom — to the ring's left point, and
  // leaves along the left edge. The sea at the bottom stays open: Lord
  // Turtleshell rises there on wave 20.
  entrances: [
    [
      { x: 0, y: 230 },
      { x: 250, y: 230 },
      { x: 250, y: 130 },
      { x: 640, y: 130 },
    ],
    [
      { x: 1030, y: 480 },
      { x: 1030, y: 130 },
      { x: 640, y: 130 },
    ],
  ],
  path: [
    { x: 640, y: 130 },
    { x: 735, y: 155 },
    { x: 805, y: 225 },
    { x: 830, y: 320 },
    { x: 805, y: 415 },
    { x: 735, y: 485 },
    { x: 640, y: 510 },
    { x: 545, y: 485 },
    { x: 475, y: 415 },
    { x: 450, y: 320 },
    { x: 360, y: 320 },
    { x: 360, y: 470 },
    { x: 0, y: 470 },
  ],
  plots: [
    // Laid for reach (spec 2026-10-12): every plot as close to the lanes as the
    // 58 px rule allows, outside the ring (whose inside is the sakura trees and
    // the orb) and off the sea where Lord Turtleshell rises.
    // Left: between the left lane, the ring and the tail — the busiest ground.
    { id: 0, pos: { x: 420, y: 440 } },
    { id: 1, pos: { x: 300, y: 400 } },
    { id: 2, pos: { x: 360, y: 240 } },
    { id: 3, pos: { x: 240, y: 290 } },
    { id: 4, pos: { x: 170, y: 410 } },
    { id: 5, pos: { x: 60, y: 350 } },
    { id: 6, pos: { x: 190, y: 170 } },
    { id: 7, pos: { x: 480, y: 210 } },
    // Under the tail and the ring, either side of the sea.
    { id: 8, pos: { x: 160, y: 540 } },
    { id: 9, pos: { x: 290, y: 530 } },
    { id: 10, pos: { x: 510, y: 550 } },
    // Right of the ring (clear of the tower panel, which starts at x 1070).
    { id: 11, pos: { x: 860, y: 200 } },
    { id: 12, pos: { x: 890, y: 320 } },
    { id: 13, pos: { x: 830, y: 480 } },
  ],
  // The boss level: a bigger purse than the four before it.
  startCash: 2000,
  startLives: 5,
  coinReward: 0,
  expReward: 0,
  towerHp: HUB_TOWER_HP,
  waves: [
    { groups: [g('winger', 4, 3)] },
    { groups: [g('twilight', 3, 2.6), g('winger', 3, 2.4, 4)] },
    { groups: [g('nightfly', 3, 3)] },
    { groups: [g('halolight', 1, 1), g('winger', 4, 2, 4)] },
    { groups: [g('twilight', 6, 1.8)] },
    { groups: [g('nightfly', 6, 2), g('winger', 6, 1.4, 4)] },
    { groups: [g('halolight', 2, 4), g('twilight', 4, 1.8, 5)] },
    { groups: [g('winger', 14, 1)] },
    { groups: [g('nightfly', 7, 1.7), g('twilight', 5, 1.6, 6)] },
    { groups: [g('halolight', 3, 4), g('nightfly', 4, 2.2, 6)] },
    { groups: [g('twilight', 12, 1.2)] },
    { groups: [g('winger', 12, 1), g('halolight', 2, 4, 5)] },
    { groups: [g('nightfly', 9, 1.5), g('twilight', 6, 1.4, 5)] },
    { groups: [g('halolight', 3, 3.5), g('winger', 8, 1.1, 5)] },
    { groups: [g('twilight', 9, 1.3), g('nightfly', 6, 1.6, 5)] },
    { groups: [g('halolight', 3, 3.5), g('twilight', 6, 1.4, 6)] },
    { groups: [g('nightfly', 10, 1.4), g('winger', 12, 1, 5)] },
    { groups: [g('halolight', 4, 3), g('nightfly', 6, 1.5, 8)] },
    // 19 — the whole sky at once.
    {
      groups: [
        g('halolight', 3, 3.5),
        g('nightfly', 8, 1.4, 8),
        g('twilight', 10, 1.2, 14),
        g('winger', 12, 0.9, 20),
      ],
    },
    // 20 — LORD TURTLESHELL floats up out of the sea, ALONE (the Shadow
    // Reaper's rule: every tower turns on an anchored boss, so anything sent
    // with him would walk past an army looking the other way).
    { groups: [g('lordturtleshell', 1, 1)] },
  ],
};

/** Left card, then right card — the order they are drawn on the EVENTS tab. */
export const ORIGIN_CHAPTERS: readonly OriginChapterDef[] = [
  {
    id: 'clouds',
    name: 'Above the Clouds',
    art: 'origin-clouds',
    levels: [SKY_UP_HIGH, RAINBOW_ARENA, SUNSET_HORIZON, SKY_ISLANDS, HEAVENS_PEAK],
  },
  {
    id: 'shadows',
    name: 'The Dark Shadows',
    art: 'origin-shadows',
    levels: [SHADOWING_MEADOW, DARK_JUNGLE, SHADOWING_TEMPLE, DARK_FOREST, EVIL_ORB_QUEST],
  },
];

/** Every Origin level, in play order. */
export const ORIGIN_LEVELS: readonly LevelDef[] = ORIGIN_CHAPTERS.flatMap((c) => c.levels);

/** Dark Hambi Coins paid by EVERY win of a level — the brief's numbers. */
export const DARK_COIN_REWARDS: Readonly<Record<number, number>> = {
  [SHADOWING_MEADOW.id]: 20,
  [DARK_JUNGLE.id]: 30,
  [SHADOWING_TEMPLE.id]: 40,
  [DARK_FOREST.id]: 50,
  // A replay of the Evil Orb's quest; the FIRST win pays DARK_COIN_FIRST_WIN.
  [EVIL_ORB_QUEST.id]: 60,
};

/** Levels that pay more on their first win: the Evil Orb's quest, 100 then 60. */
export const DARK_COIN_FIRST_WIN: Readonly<Record<number, number>> = {
  [EVIL_ORB_QUEST.id]: 100,
};

/**
 * Dark Hambi Coins for a win of `levelId`. `firstWin` is whether the level
 * had never been won before this one — read the stars BEFORE recording them.
 */
export function darkCoinRewardFor(levelId: number, firstWin = false): number {
  if (firstWin && DARK_COIN_FIRST_WIN[levelId] !== undefined) return DARK_COIN_FIRST_WIN[levelId];
  return DARK_COIN_REWARDS[levelId] ?? 0;
}

/** Light Hambi Coins paid by EVERY win of an Above the Clouds level (the user's call). */
export const LIGHT_COIN_REWARDS: Readonly<Record<number, number>> = {
  [SKY_UP_HIGH.id]: 20,
  [RAINBOW_ARENA.id]: 30,
  [SUNSET_HORIZON.id]: 40,
  [SKY_ISLANDS.id]: 50,
  // A replay of Heaven's Peak; the FIRST win pays LIGHT_COIN_FIRST_WIN.
  [HEAVENS_PEAK.id]: 60,
};

/** Levels that pay more on their first win: Heaven's Peak, 100 then 60. */
export const LIGHT_COIN_FIRST_WIN: Readonly<Record<number, number>> = {
  [HEAVENS_PEAK.id]: 100,
};

/**
 * Light Hambi Coins for a win of `levelId` (0 outside Above the Clouds).
 * `firstWin` is whether the level had never been won before this one — read
 * the stars BEFORE recording them, as for `darkCoinRewardFor`.
 */
export function lightCoinRewardFor(levelId: number, firstWin = false): number {
  if (firstWin && LIGHT_COIN_FIRST_WIN[levelId] !== undefined) return LIGHT_COIN_FIRST_WIN[levelId];
  return LIGHT_COIN_REWARDS[levelId] ?? 0;
}

/** Heaven's Peak: the chapter's boss level (Lord Turtleshell). */
export const HEAVENS_PEAK_LEVEL_ID = HEAVENS_PEAK.id;

/** Which Origin currency a chapter pays: Dark Hambi Coins or Light Hambi Coins. */
export function originCurrencyOf(chapter: OriginChapterId): 'dark' | 'light' {
  return chapter === 'clouds' ? 'light' : 'dark';
}

/** The Sky Islands pays a Light Shard on EVERY win ("everytime you beat it"). */
export const LIGHT_SHARD_LEVEL_ID = SKY_ISLANDS.id;

/** The Dark Forest pays a Dark Shard on EVERY win (the user's call). */
export const DARK_SHARD_LEVEL_ID = DARK_FOREST.id;
/** The Quest for the Evil Orb: the cutscene on every win, the orb on the first. */
export const EVIL_ORB_LEVEL_ID = EVIL_ORB_QUEST.id;

export function isOriginLevelId(id: number): boolean {
  return ORIGIN_LEVELS.some((l) => l.id === id);
}

export function findOriginLevel(id: number): LevelDef | undefined {
  return ORIGIN_LEVELS.find((l) => l.id === id);
}

export function originChapterOf(levelId: number): OriginChapterDef | undefined {
  return ORIGIN_CHAPTERS.find((c) => c.levels.some((l) => l.id === levelId));
}

export function findOriginChapter(id: string | undefined): OriginChapterDef | undefined {
  return ORIGIN_CHAPTERS.find((c) => c.id === id);
}

/**
 * Sequential within a chapter: the first level is always open, every other
 * one needs the level before it won (≥ 1★). `stars` reads `hambi.stars`.
 */
export function isOriginLevelUnlocked(levelId: number, stars: (id: number) => number): boolean {
  const chapter = originChapterOf(levelId);
  if (!chapter) return false;
  const i = chapter.levels.findIndex((l) => l.id === levelId);
  return i === 0 || stars(chapter.levels[i - 1].id) >= 1;
}

/** The level after this one in its chapter, if any. */
export function nextOriginLevel(levelId: number): LevelDef | undefined {
  const chapter = originChapterOf(levelId);
  if (!chapter) return undefined;
  const i = chapter.levels.findIndex((l) => l.id === levelId);
  return chapter.levels[i + 1];
}

/**
 * Whether the Origin Event is running. Ghosting and Halo Hunting (spec
 * 2026-10-29) can only be earned while it is — flip this to false when the
 * event ends, and players who earned them keep them forever.
 */
export const ORIGIN_EVENT_LIVE = true;

export function originEventLive(): boolean {
  return ORIGIN_EVENT_LIVE;
}
